/***
*
*   EVENT/LIST
*   List all the events in the group
*
**********/

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Table, Input, Select, Space, Typography, Button, Breadcrumb,
  Empty, Modal, message, Tooltip, Row, Col
} from 'antd';
import {
  ThunderboltOutlined, SearchOutlined, DeleteOutlined,
  EyeOutlined, ReloadOutlined, HomeOutlined, LineChartOutlined
} from '@ant-design/icons';
import { Animate, Chart } from 'components/lib';
import { fetchEvents, invalidateEventsCache } from '../../store/slices/eventsSlice';
import { fetchUserEmails } from '../../store/slices/usersSlice';
import { getCacheKey } from '../../store/cacheUtils';
import Axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

export function Events() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { name: eventName } = useParams();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [offset, setOffset] = useState(0);

  const eventsState = useSelector((state) => state.events);
  const usersState = useSelector((state) => state.users);

  const limit = 25;
  const cacheKey = getCacheKey({
    search: searchInput,
    offset,
    limit,
    name: eventName,
    user: userFilter
  });

  const eventsData = eventsState.data[cacheKey];
  const loading = eventsState.loading;
  const userEmails = usersState.emails;

  useEffect(() => {
    dispatch(fetchUserEmails());
  }, [dispatch]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchEvents({
        search: searchInput,
        offset,
        limit,
        name: eventName,
        user: userFilter
      }));
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch, searchInput, offset, limit, eventName, userFilter]);

  const handleRefresh = () => {
    dispatch(invalidateEventsCache());
    dispatch(fetchEvents({ search: searchInput, offset, limit, name: eventName, user: userFilter }));
  };

  const handleDelete = (record) => {
    confirm({
      title: 'Delete Event',
      content: 'Are you sure you want to delete this event?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await Axios.delete('/api/event/', { data: { id: record.id } });
          message.success('Event deleted');
          handleRefresh();
        } catch {
          message.error('Failed to delete event');
        }
      },
    });
  };

  const handleBulkDelete = () => {
    confirm({
      title: 'Delete Selected Events',
      content: `Are you sure you want to delete ${selectedRowKeys.length} events?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await Axios.delete('/api/event/', { data: { id: selectedRowKeys } });
          message.success('Events deleted');
          setSelectedRowKeys([]);
          handleRefresh();
        } catch {
          message.error('Failed to delete events');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Event Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button type="link" onClick={() => navigate(`/events/${eventName}/${record.id}`)}>
          {text}
        </Button>
      ),
    },
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div>
          <Text>{record.user_name || 'N/A'}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.user_email}</Text>
        </div>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'time',
      key: 'time',
      render: (time) => time ? new Date(time).toLocaleString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/events/${eventName}/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const tableData = (eventsData?.list?.results || []).map(item => ({
    ...item,
    key: item.id,
  }));

  // Chart data
  const chartData = eventsData?.chart;

  return (
    <Animate type="pop">
      <div>
        {/* Breadcrumb */}
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item href="/dashboard"><HomeOutlined /> Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item href="/events">Event Groups</Breadcrumb.Item>
          <Breadcrumb.Item>{eventName}</Breadcrumb.Item>
        </Breadcrumb>

        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <ThunderboltOutlined style={{ marginRight: 8 }} />
              {eventName}
            </Title>
            <Text type="secondary">Total: {eventsData?.list?.total || 0} events</Text>
          </div>
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button danger onClick={handleBulkDelete}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
          </Space>
        </div>

        {/* Chart */}
        {chartData && (
          <Card 
            title={
              <Space>
                <LineChartOutlined />
                <Text strong>{eventName} events by day</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Chart type="line" data={chartData} />
          </Card>
        )}

        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Space style={{ width: '100%' }} direction="vertical" size="middle">
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Input
                  placeholder="Search events..."
                  prefix={<SearchOutlined />}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12}>
                <Select
                  placeholder="Filter by user email"
                  allowClear
                  style={{ width: '100%' }}
                  value={userFilter || undefined}
                  onChange={(val) => setUserFilter(val || '')}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {(userEmails || []).map(email => (
                    <Option key={email} value={email}>{email}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Space>
        </Card>

        {/* Table */}
        <Card>
          <Table
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              current: Math.floor(offset / limit) + 1,
              pageSize: limit,
              total: eventsData?.list?.total || 0,
              onChange: (page) => setOffset((page - 1) * limit),
              showTotal: (total) => `Total ${total} events`,
            }}
            locale={{ emptyText: <Empty description="No events found" /> }}
          />
        </Card>
      </div>
    </Animate>
  );
}